// Footer.tsx
import * as React from 'react';
import { Box, Container, Divider, IconButton, Link, Stack, Typography, Tooltip, Zoom } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { PageProps } from '../types/navigationProperties';
import { PaginatedResponse } from '../types/pagination';
import { Company } from '../services/CompaniesServiceProvider';
import logo_black_tp from '../assets/img/logo_black_tp.png';
import logo_white_tp from '../assets/img/logo_white_tp.png';
import { useTranslation } from 'react-i18next';
import {
    useNavigate
} from "react-router-dom";
import { useColorScheme } from '@mui/material/styles';

function Copyright() {
    return (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {'Copyright © '}
            <Link color="text.secondary">
                OBOSS / eiNjel
            </Link>
            &nbsp;
            {new Date().getFullYear()}
        </Typography>
    );
}

export default function Footer(props: PageProps) {
    const companies: PaginatedResponse<Company> | undefined = props.companies;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mode } = useColorScheme();

    const navigateTo = (page: string) => {
        navigate(page);
    };

    return (
        <React.Fragment>
            <Divider />
            <Container sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: { xs: 4, sm: 8 },
                      py: { xs: 8, sm: 10 },
                      textAlign: { sm: 'center', md: 'left' },
                    }} >
                <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        width: '100%',
                        justifyContent: 'space-between',
                        }} >
                    <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          minWidth: { xs: '100%', sm: '60%' },
                        }} >
                        <Box sx={{ width: { xs: '100%', sm: '60%' } }}>
                        <Typography variant="body2"
                                gutterBottom
                                sx={{ fontWeight: 600, mt: 2, fontSize: { sm: '14px', md: '16px', lg: '18px' }}} >
                            {t("footer.title")}
                        </Typography>
                        <Box>
                            <img loading="lazy" src={mode === 'dark' ? logo_white_tp : logo_black_tp} alt="oboss footer logo" style={{ width: '100px' }} />
                        </Box>
                        {companies !== undefined && companies.results !== undefined && companies.results.length > 0 ? (
                            <Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 400, mt: 1 }}>
                                        {t('footer.address')} - {companies.results[0].address}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 400, mt: 1 }}>
                                        {t('footer.email')} -
                                        <Tooltip title={t("footer.email.title")}
                                                 slots={{
                                                    transition: Zoom,
                                                 }}
                                                 slotProps={{
                                                    transition: { timeout: 300 },
                                                 }} arrow>
                                            <Link href={`mailto:${companies.results[0].email}`}>{" " + companies.results[0].email}</Link>
                                        </Tooltip>
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 400, mt: 1 }}>
                                        {t('footer.or')}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 400, mt: 1 }}>
                                        {t('footer.phone')} -
                                        <Tooltip title={t("footer.phone.title")}
                                                 slots={{
                                                    transition: Zoom,
                                                 }}
                                                 slotProps={{
                                                    transition: { timeout: 300 },
                                                 }} arrow>
                                                <Link href={`tel://${companies.results[0].phone_number}`}>{" " + companies.results[0].phone_number}</Link>
                                        </Tooltip>
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <></>
                        )}
                        </Box>
                    </Box>
                    <Box sx={{
                          display: { xs: 'flex', sm: 'flex' },
                          flexDirection: 'column',
                          marginTop: { xs: '15px', sm: 'none' },
                          gap: 1,
                        }} >
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {t("footer.visit")}
                        </Typography>
                        <Tooltip title={t("home.view") + " " + t('custom_shop.menu.link')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/custom-shop")}>
                                {t('custom_shop.menu.link')}
                            </Link>
                        </Tooltip>
                        <Tooltip title={t("home.view") + " " + t('guitar_service.menu.link')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/guitar-service")}>
                                {t('guitar_service.menu.link')}
                            </Link>
                        </Tooltip>
                        <Tooltip title={t("home.view") + " " + t('video_presentations.menu.link')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/video-presentation")}>
                                {t('video_presentations.menu.link')}
                            </Link>
                        </Tooltip>
                    </Box>
                    <Box sx={{
                          display: { xs: 'flex', sm: 'flex' },
                          marginTop: { xs: '15px', sm: 'none' },
                          flexDirection: 'column',
                          gap: 1,
                        }} >
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {t("footer.company")}
                        </Typography>
                        <Tooltip title={t("home.view") + " " + t('about.menu.link')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/about-us")}>
                                {t('about.menu.link')}
                            </Link>
                        </Tooltip>
                    </Box>
                    <Box sx={{
                          display: { xs: 'flex', sm: 'flex' },
                          marginTop: { xs: '15px', sm: 'none' },
                          flexDirection: 'column',
                          gap: 1,
                        }} >
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {t("footer.legal")}
                        </Typography>
                        <Tooltip title={t("home.view") + " " + t('footer.terms')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/terms-and-conditions")}>
                                {t('footer.terms')}
                            </Link>
                        </Tooltip>
                        <Tooltip title={t("home.view") + " " + t('footer.privacy')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/privacy-policy")}>
                                {t('footer.privacy')}
                            </Link>
                        </Tooltip>
                        <Tooltip title={t("home.view") + " " + t('footer.contact')}
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/contact-us")}>
                                {t('footer.contact')}
                            </Link>
                        </Tooltip>
                    </Box>
                </Box>
                <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: { xs: 4, sm: 8 },
                        width: '100%',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }} >
                    <div>
                        <Tooltip title={t("home.view") + " " + t('footer.privacy.policy')}
                                 placement="top"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/privacy-policy")}>
                                {t('footer.privacy.policy')}
                            </Link>
                        </Tooltip>
                        <Typography sx={{ display: 'inline', mx: 0.5, opacity: 0.5 }}>
                          &nbsp;•&nbsp;
                        </Typography>
                        <Tooltip title={t("home.view") + " " + t('footer.terms.service')}
                                 placement="top"
                                 slots={{
                                    transition: Zoom,
                                 }}
                                 slotProps={{
                                    transition: { timeout: 300 },
                                 }} arrow>
                            <Link color="text.secondary" variant="body2" sx={{ 'cursor': 'pointer' }}
                                  href={void(0)} onClick={() => navigateTo("/terms-and-conditions")}>
                                {t('footer.terms.service')}
                            </Link>
                        </Tooltip>
                        <Copyright />
                    </div>
                    {companies !== undefined && companies.results !== undefined && companies.results.length > 0 ? (
                        <Stack direction="row"
                               spacing={1}
                               useFlexGap
                               sx={{ justifyContent: 'left', color: 'text.secondary' }} >
                            <Tooltip title={t("home.view") + " facebook"}
                                     placement="top"
                                     slots={{
                                        transition: Zoom,
                                     }}
                                     slotProps={{
                                        transition: { timeout: 300 },
                                     }} arrow>
                                <IconButton color="inherit"
                                          size="medium"
                                          href={companies.results[0].facebook_link !== null ? companies.results[0].facebook_link : 'http://www.facebook.com'}
                                          aria-label="Facebook"
                                          sx={{ alignSelf: 'center' }} >
                                    <FacebookIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t("home.view") + " instagram"}
                                     placement="top"
                                     slots={{
                                        transition: Zoom,
                                     }}
                                     slotProps={{
                                        transition: { timeout: 300 },
                                     }} arrow>
                                <IconButton color="inherit"
                                          size="medium"
                                          href={companies.results[0].instagram_link !== null ? companies.results[0].instagram_link : 'http://www.instagram.com'}
                                          aria-label="Instagram"
                                          sx={{ alignSelf: 'center' }} >
                                    <InstagramIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t("home.view") + " youtube"}
                                     placement="top"
                                     slots={{
                                        transition: Zoom,
                                     }}
                                     slotProps={{
                                        transition: { timeout: 300 },
                                     }} arrow>
                                <IconButton color="inherit"
                                          size="medium"
                                          href={companies.results[0].youtube_link !== null ? companies.results[0].youtube_link : 'http://www.youtube.com'}
                                          aria-label="YouTube"
                                          sx={{ alignSelf: 'center' }} >
                                    <YouTubeIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ) : (<> </>)}
                </Box>
            </Container>
        </React.Fragment>
    );
}